import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";

export interface SignupRequest {
  email: string;
  password: string;
  name?: string;
  recaptcha_response?: string;
}

export const useSignup = (onError?: (verdict: string) => void) => {
  const auth = useAuth();
  const navigate = useNavigate();

  return useMutation(
    ["signup"],
    async (signupRequest: SignupRequest) =>
      fetch(ENDPOINTS.auth.signup, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(signupRequest),
      }).then(handleResponse),
    {
      onError: (error: string | { verdict: string }) => {
        if (onError && typeof error === "object" && "verdict" in error) {
          onError(error.verdict);
        } else {
          displayErrorToast(error);
        }
      },
      onSuccess: ({ access_token, user, requires_sso }) => {
        if (requires_sso) {
          displayToast("Please use SSO to sign up.", ToastType.warning);

          navigate(`/login?email=${encodeURIComponent(user.email)}&sso=true`);
          return;
        }

        auth?.login(access_token, user);
        posthog.capture("Signed Up");
        navigate(`/verify-email?email=${encodeURIComponent(user.email)}`);
        localStorage.removeItem("last_login_type");
      },
    },
  );
};
